import React, { useContext, useState, useEffect, useMemo, useRef } from 'react'
import useTranslate from '../hooks/useTranslate';
import useTypingPlaceholder from '../hooks/useTypingPlaceholder';
import { GrSearch } from "react-icons/gr";
import { FaRegCircleUser, FaRegHeart } from "react-icons/fa6";
import { FaShoppingCart, FaTruck, FaBars, FaTimes, FaPhoneAlt, FaChevronRight, FaChevronDown } from "react-icons/fa";
import { SiGoogletranslate } from "react-icons/si";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SummaryApi from '../common';
import { toast } from 'react-toastify'
import { clearUserDetails } from '../store/userSlice';
import Context from '../context';
import WishlistDrawer from './WishlistDrawer';
import ProfileDrawer from './ProfileDrawer';
import TrackOrderDrawer from './TrackOrderDrawer';
import SavedAddressDrawer from './SavedAddressDrawer'
import MyOrdersDrawer from './MyOrdersDrawer'
import PaymentDrawer from './PaymentDrawer';
import UserProfileDrawer from './UserProfileDrawer';
import service from '../common/HttpService';
import CheckoutSlider from './CheckoutSlider';

const languages = [
    { id: 1, name: "Hindi", code: "hi" },
    { id: 2, name: "English", code: "en" },
    { id: 3, name: "Marathi", code: "mr" },
    // { id: 4, name: "Tamil", code: "ta" },
    // { id: 5, name: "Urdu", code: "ur" },
    // { id: 6, name: "Bengali", code: "bn" },
    // { id: 7, name: "Gujarati", code: "gu" },
    // { id: 8, name: "Punjabi", code: "pa" },
    // { id: 9, name: "Kannada", code: "kn" },
    // { id: 10, name: "Malayalam", code: "ml" }
];

const Header = () => {
    const user = useSelector(state => state?.user?.user)
    const dispatch = useDispatch()
    const [showProfile, setShowProfile] = useState(false)
    const context = useContext(Context)
    const navigate = useNavigate()
    const searchInput = useLocation()
    const URLSearch = new URLSearchParams(searchInput?.search)
    const searchQuery = URLSearch.getAll("q")
    const [search, setSearch] = useState(searchQuery?.[0] || "")
    const [showWishlist, setShowWishlist] = useState(false)
    const [showTrackOrder, setShowTrackOrder] = useState(false)
    const [showAddresses, setShowAddresses] = useState(false)
    const [showOrders, setShowOrders] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [showProfileInfo, setShowProfileInfo] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showLangDropdown, setShowLangDropdown] = useState(false);
    const [searchType, setSearchType] = useState('all');

    // Search Dropdown States
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
    const searchDropdownRef = useRef(null);
    const [recentSearches, setRecentSearches] = useState(() => {
        const saved = localStorage.getItem('recentSearches');
        return saved ? JSON.parse(saved) : [];
    });
    const [totalSearchCount, setTotalSearchCount] = useState(0);
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);

    const { language, setLanguage, languageCode, setLanguageCode, translateText } = context;

    // ─── Typewriter animated search placeholder ───────────────────────────────
    const searchPhrases = useMemo(() => [
        "Search product here...",
        "Search for Seeds...",
        "Search for Fertilizers...",
        "Search for Pesticides...",
        "Search for Farm Equipment...",
        "Search for Crop Protection...",
        "Search for Organic Products...",
        "Search for Animal Feed...",
    ], []);
    const typingPlaceholder = useTypingPlaceholder(searchPhrases, 75, 35, 2000);

    const uiStrings = useTranslate({
        searchPlaceholder: "Search product here...",
        categoriesLabel: "Categories",
        sellOn: "Sell on FasalShop",
        bulkOrder: "Bulk Order Inquiries",
        corporateSite: "Corporate Site",
        missedCall: "Missed Call To Order",
        trackOrder: "Track Order",
        ordersLabel: "Orders",
        wishlistLabel: "Wishlist",
        loginLabel: "Login",
        logoutLabel: "Logout Account",
        agriAdvisory: "Agri-Advisory",
        helpDesc: "Need help with your crops or orders? Our experts are here.",
        signInRegister: "Sign In / Register",
        cartLabel: "Cart",
        fasalShopUser: "FasalShop User",
        brandFirst: "Fasal",
        brandSecond: "Shop"
    });

    const [categories, setCategories] = useState([]);
    const [translatedNav, setTranslatedNav] = useState([]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await service.get(SummaryApi.getCategory.url);
                if (response?.status === "1" || response?.success === "true" || response?.success === true) {
                    const catsFromApi = response?.extraData?.category || response?.data || [];

                    const apiCats = catsFromApi.map(cat => {
                        const name = cat.category || cat.name || "";
                        return {
                            name: name,
                            key: name.toUpperCase()
                        };
                    }).filter(c => c.name);

                    setCategories(apiCats);
                }
            } catch (error) {
                console.error("Error fetching categories in Header:", error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [searchInput]);

    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isMenuOpen]);

    const handleLogout = async () => {
        try {
            const fetchData = await fetch(SummaryApi.logout_user.url, {
                method: SummaryApi.logout_user.method,
            })
            const data = await fetchData.json()
            if (data.success) {
                toast.success(data.message)
            } else if (data.error) {
                toast.error(data.message)
            }
            dispatch(clearUserDetails())
            navigate("/")
        } catch (error) {
            console.error(error)
            dispatch(clearUserDetails())
            navigate("/")
        }
    }

    const handleSearch = (e) => {
        const { value } = e.target;
        setSearch(value);
        setIsSearchDropdownOpen(true);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (search && !recentSearches.includes(search)) {
            const updatedRecent = [search, ...recentSearches].slice(0, 5);
            setRecentSearches(updatedRecent);
            localStorage.setItem('recentSearches', JSON.stringify(updatedRecent));
        }
        setIsSearchDropdownOpen(false);
        setIsMenuOpen(false);
        navigate(`/search?q=${search}&searchType=${searchType}`);
    };

    const saveRecentSearch = (query) => {
        if (query && !recentSearches.includes(query)) {
            const updatedRecent = [query, ...recentSearches].slice(0, 5);
            setRecentSearches(updatedRecent);
            localStorage.setItem('recentSearches', JSON.stringify(updatedRecent));
        }
    }

    useEffect(() => {
        const fetchResults = async () => {
            if (!search.trim()) {
                setSearchResults([]);
                setTotalSearchCount(0);
                return;
            }
            setIsSearching(true);
            try {
                const formData = new FormData();
                formData.append('item', search);
                if (searchType && searchType !== 'all') formData.append('searchType', searchType);

                const res = await service.post(SummaryApi.search_product.url, formData);
                if (res && (res.success === "true" || res.success === true)) {
                    let items = res.extraData?.items || res.extraData?.product || res.extraData || [];
                    if (items && typeof items === 'object' && !Array.isArray(items)) {
                        items = Object.values(items);
                    }
                    if (!Array.isArray(items)) items = [items];

                    const backendBaseUrl = (SummaryApi.allProduct?.url || "").split('/Api')[0] || "https://www.fasalshop.com";

                    const formattedItems = items.map(item => {
                        let images = [];
                        const itemImages = item.image || item.productImage || [];
                        if (Array.isArray(itemImages)) {
                            images = itemImages;
                        } else if (typeof itemImages === 'string' && itemImages) {
                            images = itemImages.includes(',') ? itemImages.split(',') : [itemImages];
                        }

                        images = images.map(img => {
                            if (typeof img === 'string' && img && !img.startsWith('http')) {
                                const cleanPath = img.startsWith('/') ? img.substring(1) : img;
                                return `${backendBaseUrl}/${cleanPath}`;
                            } else if (img === "https://www.fasalshop.com/product-images/") {
                                return null;
                            }
                            return img;
                        }).filter(Boolean);

                        if (images.length === 0) images = [''];

                        const firstRate = (item.rates && item.rates[0]) || (item.item_rates && item.item_rates[0]) || {};

                        return {
                            ...item,
                            _id: item.id || item._id,
                            productName: item.itemname || item.productName || "",
                            productImage: images,
                            price: Number(firstRate.mrp) || Number(item.price) || 0,
                            sellingPrice: Number(firstRate.salerate) || Number(item.sellingPrice) || 0,
                        };
                    });

                    setTotalSearchCount(items.length);
                    setSearchResults(formattedItems.slice(0, 8)); // limit results in dropdown
                } else {
                    setSearchResults([]);
                    setTotalSearchCount(0);
                }
            } catch (err) {
                console.error("Error searching in dropdown:", err);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        };

        const timer = setTimeout(fetchResults, 400);
        return () => clearTimeout(timer);
    }, [search, searchType]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
                setIsSearchDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const translateNav = async () => {
            const listToTranslate = categories;
            if (!languageCode || languageCode === 'en') {
                setTranslatedNav(listToTranslate);
                return;
            }
            try {
                const translated = await Promise.all(
                    listToTranslate.map(async (cat) => ({
                        ...cat,
                        name: await translateText(cat.name, languageCode)
                    }))
                );
                setTranslatedNav(translated);
            } catch (error) {
                console.error("Error translating nav:", error);
                setTranslatedNav(listToTranslate);
            }
        };
        translateNav();
    }, [languageCode, translateText, categories]);

    // Search type options for the dropdown
    const searchTypeOptions = [
        { value: 'all', label: 'All' },
        { value: 'category', label: 'Category' },
        { value: 'brand', label: 'Brand' },
        { value: 'subcategory', label: 'Subcategory' },
    ];

    return (
        <>
            <header className='fixed w-full z-50 bg-white shadow-sm transition-all duration-300'>
                {/* Top Bar - Hidden on mobile */}
                <div className='bg-[#143d2c] text-white py-1.5 hidden md:block'>
                    <div className='container mx-auto px-6 flex justify-end items-center gap-6 text-[12px] font-medium opacity-90'>
                        <Link to="/" className='hover:text-[#00b259] transition-all'>{uiStrings.sellOn}</Link>
                        <span className='text-gray-500'>|</span>
                        <Link to="/" className='hover:text-[#00b259] transition-all'>{uiStrings.bulkOrder}</Link>
                        <span className='text-gray-400'>|</span>
                        <Link to="/" className='hover:text-[#00b259] transition-all'>{uiStrings.corporateSite}</Link>
                        <div className='bg-[#f39200] text-white px-3 py-1 rounded-sm font-bold flex items-center ml-2 shadow-sm'>
                            <FaPhoneAlt className="mr-2 text-[10px]" /> {uiStrings.missedCall}: 1800-3000-2434
                        </div>
                    </div>
                </div>

                {/* Main Header Bar */}
                <div className='bg-white py-3 md:py-4 transition-colors duration-300 border-b border-gray-100'>
                    <div className='container mx-auto px-4 md:px-6 flex flex-wrap items-center justify-between gap-y-3 gap-x-4 md:gap-10'>

                        {/* Mobile Menu Button */}
                        <button
                            className='md:hidden p-2 text-[#143d2c] hover:bg-gray-100 rounded-lg transition-all'
                            onClick={() => setIsMenuOpen(true)}
                        >
                            <FaBars size={22} />
                        </button>

                        {/* Logo Section */}
                        <Link to={"/"} className='flex-shrink-0 flex items-center mr-auto md:mr-0'>
                            <img
                                src='/fasalshop.png'
                                alt='FasalShop'
                                className='h-14 md:h-16 w-auto object-contain hover:opacity-90 transition-opacity duration-300'
                            />
                        </Link>

                        {/* Search Bar - Always visible, wraps on mobile */}
                        <div className='w-full md:w-auto md:flex-1 max-w-2xl order-last md:order-none relative' ref={searchDropdownRef}>
                            <form onSubmit={handleSearchSubmit} className='flex items-center w-full border border-gray-200 rounded-sm bg-white focus-within:ring-1 focus-within:ring-[#00b259] transition-all overflow-hidden'>
                                {/* Search Type Dropdown - All / Category / Brand / Subcategory */}
                                <div className='relative flex items-center bg-[#f4f6f9] border-r border-gray-200 hover:bg-[#e9ecef] transition-colors'>
                                    <select
                                        value={searchType}
                                        onChange={(e) => setSearchType(e.target.value)}
                                        className='appearance-none bg-transparent text-[#1f2937] text-[13px] font-medium py-[13px] md:py-[15px] pl-4 pr-10 focus:outline-none cursor-pointer h-full'
                                    >
                                        {searchTypeOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                    <div className='absolute right-4 pointer-events-none text-gray-600'>
                                        <FaChevronDown size={10} />
                                    </div>
                                </div>

                                <div className='relative flex-1 flex items-center bg-white h-full'>
                                    <input
                                        type='text'
                                        placeholder={search ? '' : typingPlaceholder}
                                        className='flex-1 outline-none pl-4 pr-4 py-2.5 md:py-[13px] text-[14px] text-gray-700 bg-transparent placeholder:text-gray-400 w-full h-full'
                                        onChange={handleSearch}
                                        onFocus={() => setIsSearchDropdownOpen(true)}
                                        value={search}
                                    />
                                </div>
                                <button type='submit' className='bg-[#f39200] hover:bg-[#d97c00] text-white flex items-center justify-center px-5 md:px-6 transition-colors self-stretch'>
                                    <GrSearch size={18} className='text-white' />
                                </button>
                            </form>

                            {/* Search Dropdown Desktop */}
                            {isSearchDropdownOpen && (
                                <div className='absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-xl rounded-md z-[100] max-h-[70vh] overflow-y-auto overflow-x-hidden'>
                                    {isSearching ? (
                                        <div className='p-4 text-center text-gray-500 text-sm'>Searching...</div>
                                    ) : search.trim() ? (
                                        <div className='p-2'>
                                            {searchResults.length > 0 ? (
                                                <div className='flex flex-col'>
                                                    {searchResults.map((item, idx) => (
                                                        <div
                                                            key={item._id || idx}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                setIsSearchDropdownOpen(false);
                                                                setIsMenuOpen(false);
                                                                saveRecentSearch(search);
                                                                navigate(`/product/${item._id || item.id}`);
                                                            }}
                                                            className='flex items-start gap-4 p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer last:border-0'
                                                        >
                                                            <div className='w-14 h-14 bg-white border border-gray-200 rounded-lg overflow-hidden flex-shrink-0 p-1 flex items-center justify-center'>
                                                                {item.productImage && item.productImage[0] && (
                                                                    <img src={item.productImage[0]} alt={item.productName || item.itemname} className='w-full h-full object-contain' />
                                                                )}
                                                            </div>
                                                            <div className='flex flex-col flex-1 mt-1'>
                                                                <span className='text-sm text-gray-800 line-clamp-2 leading-snug'>{item.productName || item.itemname}</span>
                                                                <div className='flex items-center gap-2 mt-1'>
                                                                    <span className='font-bold text-gray-900'>₹{item.sellingPrice}</span>
                                                                    {item.price > item.sellingPrice && (
                                                                        <span className='text-xs text-gray-400 line-through'>₹{item.price}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {totalSearchCount > 8 && (
                                                        <div className='p-3 bg-white sticky bottom-0 border-t border-gray-100 flex justify-center'>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setIsSearchDropdownOpen(false);
                                                                    setIsMenuOpen(false);
                                                                    saveRecentSearch(search);
                                                                    navigate(`/search?q=${search}&searchType=${searchType}`);
                                                                }}
                                                                className='bg-[#2a8b4e] hover:bg-[#1f6b3b] text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors'
                                                            >
                                                                Show all {totalSearchCount} results for '{search}'
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className='p-4 text-center text-gray-500 text-sm'>No products matched your search</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='p-4 space-y-6'>
                                            <div>
                                                <h4 className='text-sm font-semibold text-gray-800 mb-3'>Recent Searches</h4>
                                                {recentSearches.length > 0 ? (
                                                    <div className='flex flex-col gap-1'>
                                                        {recentSearches.map((rs, idx) => (
                                                            <div
                                                                key={idx}
                                                                className='flex items-center gap-2 text-sm text-gray-600 hover:text-[#00b259] cursor-pointer py-1'
                                                                onClick={() => setSearch(rs)}
                                                            >
                                                                <GrSearch className='text-gray-400' size={12} />
                                                                {rs}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <p className='text-sm text-gray-400'>No recent search</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Action Icons Section */}
                        <div className='flex items-center gap-2 md:gap-6'>



                            {/* Language Selector - Desktop */}
                            <div className='hidden xl:block relative'>
                                <div
                                    className='flex items-center gap-2 px-3 py-1.5 border border-gray-100 rounded-lg cursor-pointer group hover:border-[#00b259] transition-all bg-slate-50/50'
                                    onClick={() => setShowLangDropdown(!showLangDropdown)}
                                >
                                    <div className='bg-[#00b259] p-1 rounded-md text-white shadow-sm'>
                                        <SiGoogletranslate size={14} />
                                    </div>
                                    <span className='text-[13px] font-bold text-gray-600'>{language}</span>
                                    <FaChevronDown size={10} className={`text-gray-400 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
                                </div>

                                {showLangDropdown && (
                                    <div className='absolute top-full mt-2 left-0 w-32 bg-white border border-gray-100 rounded-xl shadow-xl z-[60] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200'>
                                        {languages.map((lang) => (
                                            <div
                                                key={lang.id}
                                                className={`px-4 py-2.5 text-xs font-bold cursor-pointer hover:bg-green-50 transition-colors ${language === lang.name ? 'text-[#00b259] bg-green-50' : 'text-gray-600'}`}
                                                onClick={() => {
                                                    setLanguage(lang.name);
                                                    setLanguageCode(lang.code);
                                                    setShowLangDropdown(false);
                                                }}
                                            >
                                                {lang.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>



                            <div
                                className='hidden lg:flex items-center gap-2 cursor-pointer group hover:text-[#00b259] transition-all'
                                onClick={() => user?.id || user?._id ? setShowTrackOrder(true) : navigate("/login")}
                            >
                                <FaTruck size={22} className='text-gray-400 group-hover:text-[#00b259]' />
                                <span className='text-[13px] font-medium text-gray-600 group-hover:text-[#00b259]'>{uiStrings.trackOrder}</span>
                            </div>

                            <div
                                className='hidden md:flex items-center gap-2 cursor-pointer group hover:text-[#00b259] transition-all'
                                onClick={() => user?.id || user?._id ? setShowWishlist(true) : navigate("/login")}
                            >
                                <div className='relative'>
                                    <FaRegHeart size={22} className='text-gray-400 group-hover:text-[#00b259]' />
                                    {(user?.id || user?._id) && (
                                        <span className='absolute -top-2.5 -right-2 bg-[#f39200] text-white text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-bold border-2 border-white shadow-sm'>
                                            {context?.wishlistCount || 0}
                                        </span>
                                    )}
                                </div>
                                <span className='text-[13px] font-medium text-gray-600 group-hover:text-[#00b259]'>{uiStrings.wishlistLabel}</span>
                            </div>

                            <div
                                className='flex items-center gap-2 cursor-pointer group hover:text-[#00b259] transition-all'
                                onClick={() => user?.id || user?._id ? context.setShowCart(true) : navigate("/login")}
                            >
                                <div className='relative'>
                                    <FaShoppingCart size={22} className='text-gray-400 group-hover:text-[#00b259]' />
                                    {(user?.id || user?._id) && (
                                        <span className='absolute -top-2.5 -right-2 bg-[#f39200] text-white text-[9px] w-5 h-5 rounded-full flex items-center justify-center font-bold border-2 border-white shadow-sm'>
                                            {context?.cartProductCount || 0}
                                        </span>
                                    )}
                                </div>
                                <span className='hidden sm:inline text-[13px] font-medium text-gray-600 group-hover:text-[#00b259]'>{uiStrings.cartLabel}</span>
                            </div>

                            <div
                                className='flex items-center gap-2 cursor-pointer group hover:text-[#00b259] transition-all relative'
                                onClick={() => user?.id || user?._id ? setShowProfile(true) : navigate("/login")}
                            >
                                <FaRegCircleUser size={22} className='text-gray-400 group-hover:text-[#00b259]' />
                                <span className='hidden sm:inline text-[13px] font-medium text-gray-600 group-hover:text-[#00b259] whitespace-nowrap overflow-hidden max-w-[100px]'>
                                    {user?.id || user?._id ? (user?.first_name || user?.name?.split(' ')[0] || uiStrings.fasalShopUser) : uiStrings.loginLabel}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop Navigation Bar */}
                <nav className='bg-white hidden md:block py-3 border-b border-gray-50 transition-colors duration-300'>
                    <div className='container mx-auto px-6 overflow-x-auto no-scrollbar'>
                        <div className='flex items-center justify-center gap-8 lg:gap-12 min-w-max'>
                            {translatedNav.length === 0 ? (
                                <div className="flex gap-8 items-center py-1">
                                    {[1, 2, 3, 4, 5, 6, 7].map(i => (
                                        <div key={i} className="h-3.5 w-16 bg-slate-100 rounded-full animate-pulse"></div>
                                    ))}
                                </div>
                            ) : (
                                translatedNav.map((item) => (
                                    <Link
                                        key={item.key}
                                        to={`/product-category?category=${item.key.toLowerCase().replace(/\s+/g, '-')}`}
                                        className='text-[11px] font-black text-slate-500 hover:text-[#00b259] transition-all tracking-[2px] whitespace-nowrap relative group/nav'
                                    >
                                        {item.name}
                                        <span className='absolute -bottom-1 left-0 w-0 h-0.5 bg-[#00b259] transition-all duration-300 group-hover/nav:w-full'></span>
                                    </Link>
                                ))
                            )}
                        </div>
                    </div>
                </nav>

                {/* Mobile Sidebar Menu (Hamburger Menu) */}
                <div
                    className={`fixed inset-0 z-[60] transform ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 md:hidden`}
                >
                    {/* Backdrop */}
                    <div
                        className={`absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
                        onClick={() => setIsMenuOpen(false)}
                    />

                    {/* Menu Content */}
                    <div className='relative w-[80%] max-sm h-full bg-white shadow-2xl flex flex-col'>
                        <div className='p-4 border-b border-gray-100 flex items-center justify-between'>
                            <div className='flex items-center'>
                                <img
                                    src='/fasalshop.png'
                                    alt='FasalShop'
                                    className='h-12 w-auto object-contain'
                                />
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                className='p-2 text-gray-500 hover:text-red-500 transition-colors'
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className='flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-6'>
                            {/* User Profile Card in Sidebar */}
                            {(user?.id || user?._id) && (
                                <div
                                    className='flex items-center gap-3 p-4 bg-green-50 rounded-2xl cursor-pointer'
                                    onClick={() => {
                                        setShowProfile(true);
                                        setIsMenuOpen(false);
                                    }}
                                >
                                    <div className='w-12 h-12 bg-[#00b259] rounded-full flex items-center justify-center text-white shadow-md'>
                                        <FaRegCircleUser size={24} />
                                    </div>
                                    <div>
                                        <p className='text-[10px] font-bold text-[#00b259] uppercase tracking-wider'>Welcome back,</p>
                                        <p className='text-sm font-bold text-gray-800 truncate max-w-[150px]'>
                                            {user?.first_name || user?.name?.split(' ')[0] || uiStrings.fasalShopUser}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Language & Track in Mobile Menu */}
                            <div className='grid grid-cols-2 gap-3 pb-6 border-b border-gray-50'>
                                <div className='relative'>
                                    <button
                                        onClick={() => setShowLangDropdown(!showLangDropdown)}
                                        className='flex flex-col items-center justify-center p-3 bg-gray-50 rounded-xl gap-1 text-gray-600'
                                    >
                                        <SiGoogletranslate size={18} className='text-[#00b259]' />
                                        <span className='text-[10px] font-medium'>{language}</span>
                                    </button>
                                    {showLangDropdown && (
                                        <div className='absolute top-full mt-2 left-0 right-0 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden'>
                                            {languages.map((lang) => (
                                                <div
                                                    key={lang.id}
                                                    className={`px-4 py-3 text-xs font-bold text-center border-b border-gray-50 last:border-0 ${language === lang.name ? 'text-[#00b259] bg-green-50' : 'text-gray-600'}`}
                                                    onClick={() => {
                                                        setLanguage(lang.name);
                                                        setLanguageCode(lang.code);
                                                        setShowLangDropdown(false);
                                                    }}
                                                >
                                                    {lang.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => { user?.id || user?._id ? setShowTrackOrder(true) : navigate("/login"); setIsMenuOpen(false); }}
                                    className='flex flex-col items-center justify-center p-3 bg-gray-50 rounded-xl gap-1 text-gray-600'
                                >
                                    <FaTruck size={18} />
                                    <span className='text-[10px] font-medium'>{uiStrings.trackOrder}</span>
                                </button>
                            </div>

                            <div className='space-y-10'>
                                {/* Quick Actions Center */}
                                <div className='grid grid-cols-2 gap-4'>
                                    <button
                                        onClick={() => { user?.id || user?._id ? setShowTrackOrder(true) : navigate("/login"); setIsMenuOpen(false); }}
                                        className='flex flex-col items-center justify-center p-5 bg-slate-50 rounded-3xl gap-2 text-slate-600 border border-gray-100 active:scale-95 transition-all'
                                    >
                                        <FaTruck size={22} className='text-[#00b259]' />
                                        <span className='text-[10px] font-black uppercase tracking-widest text-center'>{uiStrings.ordersLabel}</span>
                                    </button>
                                    <button
                                        onClick={() => { user?.id || user?._id ? setShowWishlist(true) : navigate("/login"); setIsMenuOpen(false); }}
                                        className='flex flex-col items-center justify-center p-5 bg-slate-50 rounded-3xl gap-2 text-slate-600 border border-gray-100 active:scale-95 transition-all'
                                    >
                                        <FaRegHeart size={22} className='text-red-500' />
                                        <span className='text-[10px] font-black uppercase tracking-widest text-center'>{uiStrings.wishlistLabel}</span>
                                    </button>
                                </div>

                                {/* Categories */}
                                <div>
                                    <div className='flex items-center gap-3 mb-6 px-1'>
                                        <div className='w-1.5 h-4 bg-[#00b259] rounded-full shadow-[0_0_8px_rgba(0,178,89,0.5)]'></div>
                                        <h3 className='text-[11px] font-black text-slate-400 uppercase tracking-[4px]'>{uiStrings.categoriesLabel}</h3>
                                    </div>
                                    <div className='flex flex-col gap-1'>
                                        {translatedNav.length === 0 ? (
                                            <div className="space-y-3 py-2">
                                                {[1, 2, 3, 4, 5].map(i => (
                                                    <div key={i} className="h-10 bg-slate-50 rounded-2xl w-full animate-pulse border border-slate-100/50"></div>
                                                ))}
                                            </div>
                                        ) : (
                                            translatedNav.map((item) => (
                                                <Link
                                                    key={item.key}
                                                    to={`/product-category?category=${item.key.toLowerCase().replace(/\s+/g, '-')}`}
                                                    className='flex items-center justify-between p-4 rounded-2xl hover:bg-[#00b259]/5 text-slate-700 hover:text-[#00b259] transition-all group'
                                                >
                                                    <span className='text-sm font-bold truncate uppercase tracking-widest text-[11px]'>{item.name}</span>
                                                    <FaChevronRight size={10} className='text-slate-300 group-hover:text-[#00b259] transition-transform group-hover:translate-x-1' />
                                                </Link>
                                            ))
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>

                        {/* Footer Action */}
                        <div className='p-8 bg-slate-50 border-t border-gray-100'>
                            {user?.id || user?._id ? (
                                <button
                                    onClick={handleLogout}
                                    className='w-full py-5 text-[10px] font-black text-red-500 bg-red-500/5 border-2 border-red-500/10 rounded-2xl hover:bg-red-500 hover:text-white transition-all active:scale-95 uppercase tracking-widest'
                                >
                                    {uiStrings.logoutLabel}
                                </button>
                            ) : (
                                <button
                                    onClick={() => { navigate("/login"); setIsMenuOpen(false); }}
                                    className='w-full py-5 text-[10px] font-black text-white bg-gradient-to-r from-[#00b259] to-[#008a44] rounded-2xl shadow-xl shadow-green-500/20 active:scale-95 uppercase tracking-widest transition-all'
                                >
                                    {uiStrings.signInRegister}
                                </button>
                            )}
                            <p className='text-[10px] text-center text-slate-400 font-black uppercase tracking-[3px] mt-6 opacity-60'>
                                © {new Date().getFullYear()} FasalShop
                            </p>
                        </div>
                    </div>
                </div>
            </header>

            {/* Modals placed outside header to prevent backdrop-blur positioning bugs */}
            {showWishlist && <WishlistDrawer onClose={() => setShowWishlist(false)} />}
            {showProfile && <ProfileDrawer
                onClose={() => setShowProfile(false)}
                onOpenTrackOrder={() => setShowTrackOrder(true)}
                onOpenAddresses={() => setShowAddresses(true)}
                onOpenOrders={() => setShowOrders(true)}
                onOpenPayment={() => setShowPayment(true)}
                onOpenProfileInfo={() => setShowProfileInfo(true)}
            />}
            {showTrackOrder && <TrackOrderDrawer onClose={() => setShowTrackOrder(false)} />}
            {showAddresses && <SavedAddressDrawer onClose={() => setShowAddresses(false)} />}
            {showOrders && <MyOrdersDrawer onClose={() => setShowOrders(false)} onOpenTrackOrder={() => setShowTrackOrder(true)} />}
            {showPayment && <PaymentDrawer onClose={() => setShowPayment(false)} />}
            {showProfileInfo && <UserProfileDrawer onClose={() => setShowProfileInfo(false)} />}
            {context?.showCheckout && <CheckoutSlider onClose={() => context?.setShowCheckout(false)} />}
        </>
    )
}

export default Header